// Combat visual effects drawn in the 3D viewport: missiles and spells.
//
// Every effect carries `start` (seconds, performance clock) and `duration`,
// so any component can work out how far along an effect is on any frame.

import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { coordsToPosition, headingAxes, healsAnAlly } from '../utils/viewMath'

const Z_AXIS = new THREE.Vector3(0, 0, 1)

// 0..1 while the effect is playing, negative before, >= 1 after
function progress(effect) {
  return (performance.now() / 1000 - effect.start) / effect.duration
}

function viewAxes(heading) {
  const { forward: [fx, fz], right: [rx, rz] } = headingAxes(heading)
  return { forward: new THREE.Vector3(fx, 0, fz), right: new THREE.Vector3(rx, 0, rz) }
}

// Where a character in the given party slot "stands" relative to the camera
function partySlotPosition(effect, slot, ahead = 0.3, height = -0.18) {
  const { forward, right } = viewAxes(effect.heading)
  return new THREE.Vector3(...coordsToPosition(effect.from, effect.heading))
    .addScaledVector(right, (slot - 2.5) * 0.12)
    .addScaledVector(forward, ahead)
    .add(new THREE.Vector3(0, height, 0))
}

// Body centre of the target where it is drawn (see combatFoeLayout). Models
// scale up from the floor at -0.5, so the centre height scales with them.
function targetCenter(target) {
  const [x, , z] = target.position
  const height = target.model === 'rat' ? -0.38 : -0.05
  return new THREE.Vector3(x, -0.5 + (height + 0.5) * (target.scale || 1), z)
}

function glowMaterial(color) {
  return new THREE.MeshBasicMaterial({
    color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false,
  })
}

// ---------- Missiles ----------

// Shots are fanned out across the party's front by shooter; misses sail past the target
export function Projectile({ shot }) {
  const ref = useRef()

  const { start, end, arc } = useMemo(() => {
    const { forward, right } = viewAxes(shot.heading)
    const start = partySlotPosition(shot, shot.partyIndex)
    const end = targetCenter(shot.target)
    if (!shot.hit) {
      end.addScaledVector(right, shot.partyIndex % 2 ? 0.35 : -0.35)
        .addScaledVector(forward, 0.8)
        .add(new THREE.Vector3(0, 0.1, 0))
    }
    // Sling stones loop high; arrows and bolts fly almost flat
    return { start, end, arc: shot.projectile === 'rock' ? 0.18 : 0.05 }
  }, [shot])

  useFrame(() => {
    const obj = ref.current
    if (!obj) return
    const t = progress(shot)
    obj.visible = t >= 0 && t < 1
    if (!obj.visible) return
    obj.position.lerpVectors(start, end, t)
    obj.position.y += Math.sin(Math.PI * t) * arc
    if (shot.projectile === 'rock') {
      obj.rotation.x += 0.4
      obj.rotation.y += 0.25
    } else {
      // Point along the direction of travel, including the arc
      const dir = end.clone().sub(start)
      dir.y += Math.cos(Math.PI * t) * Math.PI * arc
      obj.quaternion.setFromUnitVectors(Z_AXIS, dir.normalize())
    }
  })

  if (shot.projectile === 'rock') {
    return (
      <mesh ref={ref} visible={false}>
        <dodecahedronGeometry args={[0.03, 0]} />
        <meshStandardMaterial color="#7a7672" roughness={1} flatShading />
      </mesh>
    )
  }

  const length = shot.projectile === 'bolt' ? 0.22 : 0.35
  return (
    <group ref={ref} visible={false}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.005, 0.005, length, 6]} />
        <meshStandardMaterial color="#8b6a3e" roughness={0.8} />
      </mesh>
      <mesh position={[0, 0, length / 2]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.013, 0.04, 6]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.8} roughness={0.3} />
      </mesh>
      {[0, Math.PI / 2].map((angle) => (
        <mesh key={angle} position={[0, 0, -length / 2 + 0.03]} rotation={[0, 0, angle]}>
          <boxGeometry args={[0.035, 0.002, 0.06]} />
          <meshStandardMaterial color={shot.projectile === 'bolt' ? '#3a3a3a' : '#e8e0d0'} side={2} />
        </mesh>
      ))}
    </group>
  )
}

// ---------- Offensive spells ----------

// `travel` is the share of the duration spent flying; the rest is the impact burst
export const SPELL_LOOKS = {
  magicArrow: { color: '#d040ff', core: '#ffffff', size: 0.022, stretch: 5, travel: 0.7, burst: 0.18, trail: 5, ring: false, flicker: false },
  energyBlast: { color: '#40c8ff', core: '#ffffff', size: 0.06, stretch: 1, travel: 0.55, burst: 0.45, trail: 6, ring: true, flicker: true },
  flyingFist: { color: '#ffb030', core: '#fff2c0', size: 0.05, stretch: 1, travel: 0.6, burst: 0.3, trail: 3, ring: true, flicker: false },
  lightningBolt: { color: '#bfe4ff', core: '#ffffff', size: 0.03, stretch: 6, travel: 0.45, burst: 0.4, trail: 4, ring: true, flicker: true },
}

function spellPath(effect) {
  return { start: partySlotPosition(effect, effect.partyIndex, 0.35, -0.1), end: targetCenter(effect.target) }
}

// Position of the spell's glowing head at a given point in its flight (0..1)
function boltPosition(path, f, out) {
  out.lerpVectors(path.start, path.end, f)
  out.y += Math.sin(Math.PI * f) * 0.04
  return out
}

export function SpellBolt({ effect }) {
  const look = SPELL_LOOKS[effect.spellId]
  const path = useMemo(() => spellPath(effect), [effect])
  const bolt = useRef()
  const burst = useRef()
  const ring = useRef()
  const trail = useRef([])

  const mats = useMemo(() => ({
    glow: glowMaterial(look.color),
    core: glowMaterial(look.core),
    burst: glowMaterial(look.color),
    ring: glowMaterial(look.core),
    trail: Array.from({ length: look.trail }, () => glowMaterial(look.color)),
  }), [look])

  useEffect(() => () => [mats.glow, mats.core, mats.burst, mats.ring, ...mats.trail].forEach((m) => m.dispose()), [mats])

  useFrame(() => {
    const t = progress(effect)
    const active = t >= 0 && t < 1
    const flying = active && t < look.travel
    const f = t / look.travel

    if (bolt.current) {
      bolt.current.visible = flying
      if (flying) {
        boltPosition(path, f, bolt.current.position)
        const dir = path.end.clone().sub(path.start).normalize()
        bolt.current.quaternion.setFromUnitVectors(Z_AXIS, dir)
        const pulse = look.flicker ? 0.8 + Math.random() * 0.5 : 1
        bolt.current.scale.setScalar(pulse)
      }
    }

    // Fading afterimages strung out behind the head
    trail.current.forEach((mesh, i) => {
      if (!mesh) return
      const tf = f - (i + 1) * 0.06
      mesh.visible = flying && tf > 0
      if (mesh.visible) {
        boltPosition(path, tf, mesh.position)
        mats.trail[i].opacity = 0.5 * (1 - i / look.trail)
      }
    })

    // Impact: an expanding, fading glow (plus a shockwave ring for heavier spells)
    const b = (t - look.travel) / (1 - look.travel)
    const bursting = active && b >= 0
    if (burst.current) {
      burst.current.visible = bursting
      if (bursting) {
        burst.current.position.copy(path.end)
        burst.current.scale.setScalar(0.03 + b * look.burst)
        mats.burst.opacity = 0.9 * (1 - b)
      }
    }
    if (ring.current) {
      ring.current.visible = bursting
      if (bursting) {
        ring.current.position.copy(path.end)
        ring.current.lookAt(path.start)
        ring.current.scale.setScalar(0.05 + b * look.burst * 1.6)
        mats.ring.opacity = 0.8 * (1 - b)
      }
    }
  })

  return (
    <group>
      <group ref={bolt} visible={false}>
        <mesh scale={[1, 1, look.stretch]} material={mats.glow}><sphereGeometry args={[look.size * 1.8, 12, 10]} /></mesh>
        <mesh scale={[1, 1, look.stretch]} material={mats.core}><sphereGeometry args={[look.size * 0.8, 10, 8]} /></mesh>
      </group>
      {mats.trail.map((material, i) => (
        <mesh key={i} ref={(el) => { trail.current[i] = el }} visible={false} material={material}>
          <sphereGeometry args={[look.size * (1.2 - i * 0.15), 8, 6]} />
        </mesh>
      ))}
      <mesh ref={burst} visible={false} material={mats.burst}><sphereGeometry args={[1, 16, 12]} /></mesh>
      {look.ring && (
        <mesh ref={ring} visible={false} material={mats.ring}><ringGeometry args={[0.8, 1, 32]} /></mesh>
      )}
    </group>
  )
}

// ---------- Healing ----------

const SPARKLES = 16

// Green motes rising in front of the healed character's slot
export function HealEffect({ effect }) {
  const group = useRef()
  const material = useMemo(() => glowMaterial('#60ff90'), [])
  useEffect(() => () => material.dispose(), [material])

  const sparkles = useMemo(() => {
    const base = partySlotPosition(effect, effect.allyIndex, 0.5, -0.3)
    return Array.from({ length: SPARKLES }, (_, i) => ({
      base: base.clone().add(new THREE.Vector3((Math.random() - 0.5) * 0.12, Math.random() * 0.05, (Math.random() - 0.5) * 0.05)),
      rise: 0.18 + Math.random() * 0.15,
      phase: Math.random() * Math.PI * 2,
      lag: (i / SPARKLES) * 0.4,
    }))
  }, [effect])

  useFrame(() => {
    const t = progress(effect)
    if (!group.current) return
    group.current.visible = t >= 0 && t < 1
    if (!group.current.visible) return
    group.current.children.forEach((mesh, i) => {
      const s = sparkles[i]
      const local = Math.max(0, Math.min(1, (t - s.lag) / (1 - s.lag)))
      mesh.position.copy(s.base)
      mesh.position.y += local * s.rise
      mesh.position.x += Math.sin(s.phase + local * 6) * 0.015
      mesh.scale.setScalar(local > 0 && local < 1 ? Math.sin(Math.PI * local) : 0)
    })
    material.opacity = 1 - t * 0.5
  })

  return (
    <group ref={group} visible={false}>
      {sparkles.map((_, i) => (
        <mesh key={i} material={material}><sphereGeometry args={[0.008, 6, 4]} /></mesh>
      ))}
    </group>
  )
}

// ---------- Lighting ----------

// A single light that is always mounted (adding/removing lights forces every
// material to recompile) and follows whichever spell is currently playing
export function SpellLight({ effects }) {
  const light = useRef()
  const scratch = useMemo(() => new THREE.Vector3(), [])

  useFrame(() => {
    if (!light.current) return
    const effect = effects.find((e) => e.type === 'spell' && progress(e) >= 0 && progress(e) < 1)
    if (!effect) {
      light.current.intensity = 0
      return
    }
    const t = progress(effect)
    if (healsAnAlly(effect)) {
      light.current.color.set('#60ff90')
      light.current.position.copy(partySlotPosition(effect, effect.allyIndex, 0.5, -0.1))
      light.current.intensity = Math.sin(Math.PI * t) * 1.2
      return
    }
    const look = SPELL_LOOKS[effect.spellId]
    const path = spellPath(effect)
    light.current.color.set(look.color)
    if (t < look.travel) {
      boltPosition(path, t / look.travel, scratch)
      light.current.position.copy(scratch)
      light.current.intensity = 1.5
    } else {
      const b = (t - look.travel) / (1 - look.travel)
      light.current.position.copy(path.end)
      light.current.intensity = (1 - b) * (look.burst > 0.3 ? 4 : 2.5)
    }
  })

  return <pointLight ref={light} intensity={0} distance={4} decay={2} />
}

export function Effect({ effect }) {
  if (effect.type === 'missile') return <Projectile shot={effect} />
  if (healsAnAlly(effect)) return <HealEffect effect={effect} />
  // Anything else is aimed at a foe, so there is nothing to draw without one
  if (SPELL_LOOKS[effect.spellId] && effect.target) return <SpellBolt effect={effect} />
  return null
}
